import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Paper,
  Skeleton,
  Alert,
  CircularProgress,
  Fade,
} from '@mui/material';
import {
  TrendingUp,
  Users,
  FileText,
  Clock,
  CheckCircle,
  Award,
  Calendar,
  RefreshCw,
  AlertCircle,
  ArrowRight,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useDashboard } from '../../hooks/useDashboard';
import { useNotification } from '../../components/common/Notification';
import CustomButton from '../../components/ui/CustomButton';

// Import Certificate Dialog Components
import { CreateCertificateDialog } from '../certificates/CertificateForms';
import { certificatesService } from '../../services/certificates';

const StatCard = ({
  title,
  value,
  change,
  icon: Icon,
  color,
  progress,
  isLoading,
  onClick,
}) => {
  const { isDarkMode } = useTheme();

  if (isLoading) {
    return (
      <Card
        sx={{
          height: '100%',
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
          backdropFilter: 'blur(20px)',
          border: isDarkMode
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.2)',
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" height={40} />
              <Skeleton variant="text" width="80%" height={20} />
            </Box>
            <Skeleton variant="circular" width={56} height={56} />
          </Box>
          <Skeleton
            variant="rectangular"
            height={8}
            sx={{ borderRadius: 4, mb: 1 }}
          />
          <Skeleton variant="text" width="40%" height={16} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Fade in={true} timeout={300}>
      <Card
        sx={{
          height: '100%',
          position: 'relative',
          overflow: 'visible',
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
          backdropFilter: 'blur(20px)',
          border: isDarkMode
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.2)',
          boxShadow: isDarkMode
            ? '0 8px 32px rgba(0, 0, 0, 0.3)'
            : '0 8px 32px rgba(0, 0, 0, 0.1)',
          borderRadius: 3,
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: onClick
              ? 'translateY(-4px) scale(1.02)'
              : 'translateY(-4px)',
            boxShadow: isDarkMode
              ? `0 20px 40px rgba(0, 0, 0, 0.4), 0 0 30px ${color}20`
              : `0 20px 40px rgba(0, 0, 0, 0.15), 0 0 30px ${color}20`,
          },
        }}
        onClick={onClick}
      >
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: color,
                  mb: 0.5,
                  textShadow: `0 0 20px ${color}40`,
                }}
              >
                {value}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: isDarkMode ? '#94a3b8' : '#64748b',
                  fontWeight: 500,
                }}
              >
                {title}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `linear-gradient(135deg, ${color}20, ${color}10)`,
                border: `1px solid ${color}30`,
                boxShadow: `0 8px 16px ${color}30`,
              }}
            >
              <Icon size={24} color={color} />
            </Box>
          </Box>

          {progress !== undefined && (
            <Box sx={{ mb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: `${color}20`,
                  '& .MuiLinearProgress-bar': {
                    background: `linear-gradient(90deg, ${color}, ${color}CC)`,
                    borderRadius: 4,
                    boxShadow: `0 0 10px ${color}60`,
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  mt: 0.5,
                  display: 'block',
                  color: isDarkMode ? '#cbd5e1' : '#475569',
                  fontWeight: 500,
                }}
              >
                {progress}% of target
              </Typography>
            </Box>
          )}

          {change !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <TrendingUp
                size={14}
                color={change >= 0 ? '#10b981' : '#ef4444'}
              />
              <Typography
                variant="caption"
                sx={{
                  ml: 0.5,
                  color: change >= 0 ? '#10b981' : '#ef4444',
                  fontWeight: 600,
                }}
              >
                {change >= 0 ? '+' : ''}
                {change}% from last month
              </Typography>
            </Box>
          )}

          {onClick && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 12,
                right: 12,
                opacity: 0.6,
                transition: 'opacity 0.2s',
                '.MuiCard-root:hover &': {
                  opacity: 1,
                },
              }}
            >
              <ArrowRight size={16} color={color} />
            </Box>
          )}
        </CardContent>
      </Card>
    </Fade>
  );
};

const DashboardPage = () => {
  const { user, isAdmin, isOffice } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const {
    dashboardData,
    recentActivities,
    isLoading,
    error,
    lastUpdated,
    refreshData,
  } = useDashboard();
  const { showNotification, NotificationComponent } = useNotification();

  // Certificate Dialog State
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Handle stat card clicks
  const handleStatCardClick = type => {
    switch (type) {
      case 'certificates':
        navigate('/certificates');
        showNotification('Loading certificates...', 'info');
        break;
      case 'users':
        if (isAdmin()) {
          navigate('/users');
          showNotification('Loading user management...', 'info');
        } else {
          showNotification('Access denied: Admin privileges required', 'error');
        }
        break;
      case 'pending':
        navigate('/certificates?status=pending');
        showNotification('Loading pending certificates...', 'info');
        break;
      case 'statistics':
        navigate('/statistics');
        showNotification('Loading statistics...', 'info');
        break;
      default:
        break;
    }
  };

  // Handle creating new certificate
  const handleCreateCertificate = async certificateData => {
    try {
      const newCertificate =
        await certificatesService.createCertificate(certificateData);

      // Refresh dashboard data to reflect the new certificate
      refreshData();

      showNotification('Certificate created successfully!', 'success');
      return newCertificate;
    } catch (error) {
      console.error('Error creating certificate:', error);
      throw error; // Re-throw to let the dialog handle the error
    }
  };

  // Calculate stats from real data
  const stats = dashboardData
    ? [
        {
          title: 'Total Certificates',
          value: dashboardData.totalCertificates.toLocaleString(),
          change: parseFloat(dashboardData.monthlyGrowth),
          icon: FileText,
          color: '#3b82f6',
          progress: Math.min(
            100,
            (dashboardData.totalCertificates / 2000) * 100
          ),
          onClick: () => handleStatCardClick('certificates'),
        },
        {
          title: 'Active Users',
          value: dashboardData.activeUsers.toLocaleString(),
          change: parseFloat(dashboardData.userGrowth),
          icon: Users,
          color: '#10b981',
          progress: Math.min(100, (dashboardData.activeUsers / 100) * 100),
          onClick: () => handleStatCardClick('users'),
        },
      ]
    : [];

  const getActivityIcon = type => {
    switch (type) {
      case 'create':
        return <FileText size={16} />;
      case 'user':
        return <Users size={16} />;
      case 'approve':
        return <CheckCircle size={16} />;
      case 'system':
        return <Award size={16} />;
      default:
        return <Clock size={16} />;
    }
  };

  const getActivityColor = type => {
    switch (type) {
      case 'create':
        return '#3b82f6';
      case 'user':
        return '#10b981';
      case 'approve':
        return '#06b6d4';
      case 'system':
        return '#8b5cf6';
      default:
        return '#64748b';
    }
  };

  return (
    <Box sx={{ backgroundColor: 'transparent' }}>
      {/* Welcome Header */}
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: isDarkMode ? '#f1f5f9' : '#1e293b',
              textShadow: isDarkMode
                ? '0 0 20px rgba(59, 130, 246, 0.3)'
                : 'none',
            }}
          >
            Welcome back, {user?.FirstName}! 👋
          </Typography>

          {lastUpdated && (
            <Typography
              variant="caption"
              sx={{
                color: isDarkMode ? '#64748b' : '#94a3b8',
                display: 'flex',
                alignItems: 'center',
                mt: 1,
              }}
            >
              <Clock size={12} style={{ marginRight: 4 }} />
              Last updated: {lastUpdated.toLocaleTimeString()}
            </Typography>
          )}
        </Box>

        <CustomButton
          variant="outlined"
          onClick={() => {
            refreshData();
            showNotification('Dashboard data refreshed', 'success');
          }}
          disabled={isLoading}
          startIcon={
            isLoading ? <CircularProgress size={16} /> : <RefreshCw size={16} />
          }
          sx={{
            borderColor: isDarkMode ? '#374151' : '#d1d5db',
            color: isDarkMode ? '#e5e7eb' : '#374151',
            '&:hover': {
              borderColor: '#3b82f6',
              color: '#3b82f6',
              background: 'rgba(59, 130, 246, 0.1)',
            },
          }}
        >
          Refresh Data
        </CustomButton>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <CustomButton
              size="small"
              onClick={() => {
                refreshData();
                showNotification('Retrying data fetch...', 'info');
              }}
              startIcon={<RefreshCw size={14} />}
            >
              Retry
            </CustomButton>
          }
        >
          <Typography variant="body2">
            <strong>Error loading dashboard:</strong> {error}
          </Typography>
        </Alert>
      )}

      {/* Stats Grid with Click Actions */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {(isLoading ? Array(4).fill({}) : stats).map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <StatCard {...stat} isLoading={isLoading} />
          </Grid>
        ))}
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              height: '100%',
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
              backdropFilter: 'blur(20px)',
              border: isDarkMode
                ? '1px solid rgba(148, 163, 184, 0.1)'
                : '1px solid rgba(148, 163, 184, 0.2)',
              boxShadow: isDarkMode
                ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                : '0 8px 32px rgba(0, 0, 0, 0.1)',
              borderRadius: 3,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Award size={24} color="#3b82f6" />
                <Typography
                  variant="h6"
                  sx={{
                    ml: 1,
                    fontWeight: 600,
                    color: isDarkMode ? '#f1f5f9' : '#1e293b',
                  }}
                >
                  Quick Actions
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {isOffice() && (
                  <CustomButton
                    variant="gradient"
                    gradient="primary"
                    fullWidth
                    startIcon={<FileText size={18} />}
                    onClick={() => {
                      setCreateDialogOpen(true);
                      showNotification(
                        'Opening certificate creation dialog...',
                        'info'
                      );
                    }}
                    sx={{
                      background:
                        'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                      boxShadow: '0 4px 15px rgba(59, 130, 246, 0.4)',
                      '&:hover': {
                        background:
                          'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                        boxShadow: '0 8px 25px rgba(59, 130, 246, 0.6)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    Create New Certificate
                  </CustomButton>
                )}

                <CustomButton
                  variant="gradient"
                  gradient="secondary"
                  fullWidth
                  startIcon={<FileText size={18} />}
                  onClick={() => {
                    navigate('/certificates');
                    showNotification('Loading certificates...', 'info');
                  }}
                  sx={{
                    background:
                      'linear-gradient(135deg, #06b6d4 0%, #10b981 100%)',
                    boxShadow: '0 4px 15px rgba(6, 182, 212, 0.4)',
                    '&:hover': {
                      background:
                        'linear-gradient(135deg, #0891b2 0%, #059669 100%)',
                      boxShadow: '0 8px 25px rgba(6, 182, 212, 0.6)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  View All Certificates
                </CustomButton>

                {isAdmin() && (
                  <CustomButton
                    variant="gradient"
                    gradient="success"
                    fullWidth
                    startIcon={<Users size={18} />}
                    onClick={() => {
                      navigate('/users');
                      showNotification('Loading user management...', 'info');
                    }}
                    sx={{
                      background:
                        'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                      boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)',
                      '&:hover': {
                        background:
                          'linear-gradient(135deg, #7c3aed 0%, #db2777 100%)',
                        boxShadow: '0 8px 25px rgba(139, 92, 246, 0.6)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    Manage Users
                  </CustomButton>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity with Real Data */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              maxHeight: '60vh', // Responsive max height
              width: 500,
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
              backdropFilter: 'blur(20px)',
              border: isDarkMode
                ? '1px solid rgba(148, 163, 184, 0.1)'
                : '1px solid rgba(148, 163, 184, 0.2)',
              boxShadow: isDarkMode
                ? '0 8px 32px rgba(0, 0, 0, 0.3)'
                : '0 8px 32px rgba(0, 0, 0, 0.1)',
              borderRadius: 3,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CardContent
              sx={{
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 3,
                  flexShrink: 0,
                }}
              >
                <Clock size={24} color="#06b6d4" />
                <Typography
                  variant="h6"
                  sx={{
                    ml: 1,
                    fontWeight: 600,
                    color: isDarkMode ? '#f1f5f9' : '#1e293b',
                  }}
                >
                  Recent Activity
                </Typography>
              </Box>

              {/* Scrollable Content */}
              <Box
                sx={{
                  overflowY: 'auto',
                  flex: 1,
                  pr: 1,
                  minHeight: 0, // important for flex scroll containers
                }}
              >
                {isLoading ? (
                  <Box>
                    {Array(4)
                      .fill({})
                      .map((_, index) => (
                        <Box
                          key={index}
                          sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
                        >
                          <Skeleton
                            variant="circular"
                            width={40}
                            height={40}
                            sx={{ mr: 2 }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Skeleton variant="text" width="70%" height={20} />
                            <Skeleton variant="text" width="50%" height={16} />
                          </Box>
                        </Box>
                      ))}
                  </Box>
                ) : recentActivities.length > 0 ? (
                  <List sx={{ p: 0 }}>
                    {recentActivities.map((activity, index) => (
                      <Fade
                        in={true}
                        timeout={300 + index * 100}
                        key={activity.id}
                      >
                        <ListItem
                          sx={{
                            px: 0,
                            py: 1.5,
                            borderBottom:
                              index < recentActivities.length - 1
                                ? isDarkMode
                                  ? '1px solid rgba(148, 163, 184, 0.1)'
                                  : '1px solid rgba(148, 163, 184, 0.2)'
                                : 'none',
                            transition: 'all 0.2s ease-in-out',
                            cursor: 'pointer',
                            borderRadius: 2,
                            '&:hover': {
                              background: isDarkMode
                                ? 'rgba(59, 130, 246, 0.05)'
                                : 'rgba(59, 130, 246, 0.02)',
                              transform: 'translateX(4px)',
                            },
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                background: `linear-gradient(135deg, ${getActivityColor(activity.type)}, ${getActivityColor(activity.type)}CC)`,
                                width: 40,
                                height: 40,
                                boxShadow: `0 4px 12px ${getActivityColor(activity.type)}30`,
                              }}
                            >
                              {getActivityIcon(activity.type)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 600,
                                  color: isDarkMode ? '#f1f5f9' : '#1e293b',
                                }}
                              >
                                {activity.title}
                              </Typography>
                            }
                            secondary={
                              <Box sx={{ mt: 0.5 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    mb: 0.5,
                                    color: isDarkMode ? '#94a3b8' : '#64748b',
                                  }}
                                  component="div"
                                >
                                  {activity.description}
                                </Typography>
                                <Chip
                                  label={activity.time}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    background: `linear-gradient(135deg, ${getActivityColor(activity.type)}20, ${getActivityColor(activity.type)}10)`,
                                    color: getActivityColor(activity.type),
                                    border: `1px solid ${getActivityColor(activity.type)}30`,
                                  }}
                                />
                              </Box>
                            }
                          />
                        </ListItem>
                      </Fade>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <AlertCircle
                      size={48}
                      color={isDarkMode ? '#64748b' : '#94a3b8'}
                      style={{ marginBottom: 16 }}
                    />
                    <Typography
                      variant="body1"
                      sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                    >
                      No recent activity found
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: isDarkMode ? '#64748b' : '#94a3b8', mb: 2 }}
                    >
                      Start creating certificates to see activity here
                    </Typography>
                    {isOffice() && (
                      <CustomButton
                        variant="gradient"
                        gradient="primary"
                        size="small"
                        startIcon={<FileText size={16} />}
                        onClick={() => {
                          setCreateDialogOpen(true);
                          showNotification(
                            'Opening certificate creation dialog...',
                            'info'
                          );
                        }}
                      >
                        Create First Certificate
                      </CustomButton>
                    )}
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* System Overview with Real Data */}
      <Box sx={{ mt: 4 }}>
        <Paper
          sx={{
            p: 3,
            borderRadius: 3,
            background: isDarkMode
              ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
            backdropFilter: 'blur(20px)',
            border: isDarkMode
              ? '1px solid rgba(148, 163, 184, 0.1)'
              : '1px solid rgba(148, 163, 184, 0.2)',
            boxShadow: isDarkMode
              ? '0 8px 32px rgba(0, 0, 0, 0.3)'
              : '0 8px 32px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Calendar size={24} color="#8b5cf6" />
            <Typography
              variant="h6"
              sx={{
                ml: 1,
                fontWeight: 600,
                color: isDarkMode ? '#f1f5f9' : '#1e293b',
              }}
            >
              System Overview
            </Typography>
          </Box>

          {dashboardData && (
            <Grid container spacing={3}>
              <Grid item xs={6} md={3}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Typography
                    variant="h5"
                    sx={{ color: '#3b82f6', fontWeight: 700, mb: 0.5 }}
                  >
                    {dashboardData.totalCertificates}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                  >
                    Total Certificates
                  </Typography>
                </Box>
              </Grid>
              {isAdmin() ? (
                <Grid item xs={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography
                      variant="h5"
                      sx={{ color: '#10b981', fontWeight: 700, mb: 0.5 }}
                    >
                      {dashboardData.activeUsers}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}
                    >
                      Active Users
                    </Typography>
                  </Box>
                </Grid>
              ) : (
                <Grid item xs={6} md={3}></Grid>
              )}
            </Grid>
          )}

          <Box
            sx={{
              mt: 3,
              pt: 3,
              borderTop: isDarkMode
                ? '1px solid rgba(148, 163, 184, 0.1)'
                : '1px solid rgba(148, 163, 184, 0.2)',
            }}
          >
            {isAdmin() ? (
              <Typography
                variant="body2"
                sx={{ color: isDarkMode ? '#cbd5e1' : '#475569' }}
              >
                <strong>Admin Access:</strong> You have full system access
                including user management, certificate oversight, and analytics.
                Click on the stat cards above to navigate to different sections.
              </Typography>
            ) : (
              <Typography
                variant="body2"
                sx={{ color: isDarkMode ? '#cbd5e1' : '#475569' }}
              >
                <strong>Office Access:</strong> You can create, edit, and manage
                certificates. Use the quick actions and stat cards to navigate
                to different sections of the system.
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Create Certificate Dialog */}
      <CreateCertificateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSave={handleCreateCertificate}
        isDarkMode={isDarkMode}
      />

      {/* Notification Component */}
      <NotificationComponent />
    </Box>
  );
};

export default DashboardPage;
